'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useT } from '@/components/i18n-provider';
import { startFeaturedMcpOauthFlow } from './oauth-launcher';
import { dispatchIntegrationChanged } from './integration-events';

type AuthType = 'none' | 'api_key' | 'api_key_query' | 'oauth2';

interface SetupField {
  name: string;
  label: string;
  placeholder?: string;
  help?: string;
  kind?: 'identifier' | 'url';
  pattern?: string;
}

interface ConnectMode {
  id: string;
  label: string;
  isDefault?: boolean;
  authType: 'api_key' | 'api_key_query' | 'oauth2';
  requiredCredential?: string;
  help?: string;
}

interface FeaturedMcp {
  id: string;
  name: string;
  iconSlug: string;
  transport?: 'sse' | 'http' | 'auto';
  mcpUrl?: string;
  mcpUrlTemplate?: string;
  authType?: AuthType;
  connectModes?: ConnectMode[];
  requiredCredential?: string;
  setupFields?: SetupField[];
  requiresExplicitTools?: boolean;
  requiresStaticClient?: boolean;
  hasPlatformOAuthClient?: boolean;
  byoOAuth?: {
    registrationUrl: string;
    notes?: string[];
  };
  prompt?: { name?: string; priority?: number; text: string };
}

interface Props {
  catalog: FeaturedMcp;
  agentUserId: string;
  agentConfigId: string;
  onChanged: () => void;
  connectNonce?: number;
}

export function FeaturedMcpConnectPanel({
  catalog,
  agentUserId,
  agentConfigId,
  onChanged,
  connectNonce = 0,
}: Props) {
  const t = useT();
  const defaultMode = useMemo(
    () => catalog.connectModes?.find((m) => m.isDefault) ?? catalog.connectModes?.[0],
    [catalog.connectModes],
  );
  const [modeId, setModeId] = useState(defaultMode?.id);
  const [values, setValues] = useState<Record<string, string>>({});
  const [apiKey, setApiKey] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scope, setScope] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastConnectNonce = useRef(connectNonce);

  const mode = modeId ? catalog.connectModes?.find((m) => m.id === modeId) : undefined;
  const effectiveAuth = (mode?.authType ?? catalog.authType ?? 'none') as AuthType;
  const supportsByoOAuth = effectiveAuth === 'oauth2' && (catalog.requiresStaticClient || !!catalog.byoOAuth);
  const needsByoOAuthClient = supportsByoOAuth && catalog.requiresStaticClient && catalog.hasPlatformOAuthClient === false;
  const hasVisibleControls =
    (catalog.connectModes?.length ?? 0) > 0 ||
    (catalog.setupFields?.length ?? 0) > 0 ||
    effectiveAuth === 'api_key' ||
    effectiveAuth === 'api_key_query' ||
    supportsByoOAuth;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const collected: Record<string, string> = {};
      for (const field of catalog.setupFields ?? []) {
        const value = (values[field.name] ?? '').trim();
        if (!value) throw new Error(`Please fill in "${field.label}"`);
        if ((field.kind ?? 'identifier') === 'identifier' && field.pattern) {
          const re = new RegExp(field.pattern);
          if (!re.test(value)) throw new Error(`"${field.label}" does not match the required format`);
        }
        if (field.kind === 'url') new URL(value);
        collected[field.name] = value;
      }

      if ((effectiveAuth === 'api_key' || effectiveAuth === 'api_key_query') && !apiKey.trim()) {
        throw new Error('API key is required');
      }

      if (effectiveAuth === 'oauth2') {
        if (needsByoOAuthClient && !clientId.trim()) {
          setAdvancedOpen(true);
          throw new Error('Add this provider OAuth app credentials first.');
        }
        if (clientId.trim()) {
          const response = await fetch(`/api/agents/${agentUserId}/mcp/oauth-client`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              catalogId: catalog.id,
              clientId: clientId.trim(),
              clientSecret: clientSecret.trim() || undefined,
              scope: scope.trim() || undefined,
            }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error ?? `oauth_client_save_${response.status}`);
        }
        await startFeaturedMcpOauthFlow({
          catalog,
          agentUserId,
          setupFields: collected,
        });
        return;
      }

      const body: Record<string, unknown> = {
        name: catalog.id,
        catalogId: catalog.id,
        authType: effectiveAuth,
        transport: catalog.transport ?? 'auto',
        setupFields: collected,
      };
      if (catalog.mcpUrlTemplate) body.mcpUrlTemplate = catalog.mcpUrlTemplate;
      if (catalog.mcpUrl) body.url = catalog.mcpUrl;
      if (mode?.requiredCredential) body.requiredCredential = mode.requiredCredential;
      if (catalog.requiredCredential) body.requiredCredential = catalog.requiredCredential;
      if (catalog.prompt) body.prompt = catalog.prompt;
      if (effectiveAuth === 'api_key' || effectiveAuth === 'api_key_query') {
        body.apiKey = apiKey.trim();
      }

      const response = await fetch(`/api/agents/${agentUserId}/mcp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error ?? `save_failed_${response.status}`);
      dispatchIntegrationChanged({ reason: 'mcp_save', ref: catalog.id, agentConfigId });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to connect MCP server');
      window.dispatchEvent(
        new CustomEvent('integration-connect-failed', {
          detail: { kind: 'mcp', ref: catalog.id },
        }),
      );
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (connectNonce === lastConnectNonce.current) return;
    lastConnectNonce.current = connectNonce;
    submit();
    // submit intentionally reads current form state at the moment the header
    // toggle is flipped on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectNonce]);

  useEffect(() => {
    if (needsByoOAuthClient) setAdvancedOpen(true);
  }, [needsByoOAuthClient]);

  if (!hasVisibleControls && !busy && !error) return null;

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      {catalog.connectModes && catalog.connectModes.length > 0 && (
        <div className="space-y-1">
          {catalog.connectModes.map((m) => (
            <label key={m.id} className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name={`mcp-connect-mode-${catalog.id}`}
                checked={modeId === m.id}
                onChange={() => {
                  setModeId(m.id);
                  setError(null);
                }}
                className="mt-0.5"
              />
              <span>
                {m.label}
                {m.help && <span className="block text-xs text-muted-foreground">{m.help}</span>}
              </span>
            </label>
          ))}
        </div>
      )}

      {(catalog.setupFields ?? []).map((field) => (
        <div key={field.name}>
          <label className="text-xs font-medium">{field.label}</label>
          <input
            type={field.kind === 'url' ? 'url' : 'text'}
            placeholder={field.placeholder}
            value={values[field.name] ?? ''}
            onChange={(event) => {
              setValues((prev) => ({ ...prev, [field.name]: event.target.value }));
              setError(null);
            }}
            className="input mt-1 w-full text-sm"
          />
          {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
        </div>
      ))}

      {(effectiveAuth === 'api_key' || effectiveAuth === 'api_key_query') && (
        <div>
          <label className="text-xs font-medium">{t('settings.authApiKey')}</label>
          <input
            type="password"
            value={apiKey}
            onChange={(event) => {
              setApiKey(event.target.value);
              setError(null);
            }}
            className="input mt-1 w-full text-sm"
          />
        </div>
      )}

      {supportsByoOAuth && (
        <div className="space-y-2">
          {needsByoOAuthClient && (
            <p className="text-xs text-muted-foreground">
              {t('agentTools.requiresRegisteredOauthApp')}
            </p>
          )}
          <button
            type="button"
            onClick={() => setAdvancedOpen((value) => !value)}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {advancedOpen ? t('settings.nsfwHide') : t('settings.nsfwShow')} {t('agentTools.advancedConnectionSettings')}
          </button>
          {advancedOpen && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">
                {t('agentTools.byoOauthOptionalNote')}
              </p>
              {catalog.byoOAuth?.registrationUrl && (
                <a
                  href={catalog.byoOAuth.registrationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  {t('agentTools.openProviderAppSetup')}
                </a>
              )}
              <div>
                <label className="text-xs font-medium">{t('settings.clientId')}</label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(event) => {
                    setClientId(event.target.value);
                    setError(null);
                  }}
                  className="input mt-1 w-full text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium">{t('agentTools.clientSecret')}</label>
                <input
                  type="password"
                  value={clientSecret}
                  onChange={(event) => {
                    setClientSecret(event.target.value);
                    setError(null);
                  }}
                  className="input mt-1 w-full text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium">{t('agentTools.scopeOverride')}</label>
                <input
                  type="text"
                  value={scope}
                  onChange={(event) => {
                    setScope(event.target.value);
                    setError(null);
                  }}
                  placeholder={t('agentTools.optional')}
                  className="input mt-1 w-full text-sm"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner size="sm" tone="muted" />
          <span>Connecting...</span>
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
